import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { canShowCutleryOptInWidget } from "@cart-client/helpers/Cart";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// analytics
import { clickCutleryOptInWidget, impressionCutleryOptInWidget } from "@cart-client/analytics/events";

//utils
import { SessionVisibility, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { buildCutleryUpdatePayload, effectiveServerCartComputer, isMROFlow } from "@cart-client/helpers/MROFoodCart";

//types
import { BOOLEAN_AS_STRING, ReviewedCartApiResponse } from "@swiggy-private/cart-types";

//helpers
import { getNestedValue } from "@swiggy-private/js-utils";

const CUTLERY_FTUE_FREQUENCY = "cutlery_ftue_frequency";

interface ICutleryOptInOutHook {
    showCutleryTile: boolean;
    isCutleryOpted: boolean;
    toggleCutlery: () => Promise<void>;
    handleNudgeClosed: Dispatch<SetStateAction<boolean>>;
    nudgeClosed: boolean;
    isFtueForCutlery?: boolean;
}

interface ICutleryOptInOutProps {
    restaurantId?: string | number;
}

export function useCutleryOptInOut({ restaurantId }: ICutleryOptInOutProps = {}): ICutleryOptInOutHook {
    const [nudgeClosed, handleNudgeClosed] = useState<boolean>(false);
    const [isFtueForCutlery, setIsFtueForCutlery] = useState<boolean>(false);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { doUpdateCart } = useContext(UpdateCartContext);
    const { data } = useContext(CartFlowContext);
    const { reviewedCart: cartResponse, localCart } = cartState;

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: CUTLERY_FTUE_FREQUENCY,
        maxFrequency: 2,
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION,
    });

    const effectiveCartData = useMemo(
        () => effectiveServerCartComputer(cartResponse, isMROFlow(), restaurantId),
        [cartResponse, restaurantId],
    );

    const isCutleryOpted = useMemo(() => {
        return getNestedValue(effectiveCartData, "cart_saved_preferences.is_cutlery_opted") === BOOLEAN_AS_STRING.TRUE;
    }, [effectiveCartData]);

    const showCutleryTile = canShowCutleryOptInWidget(effectiveCartData as ReviewedCartApiResponse);

    const toggleCutlery = useCallback(async () => {
        clickCutleryOptInWidget(clickEvent, !isCutleryOpted ? 1 : 0);
        const isOptedValue = isCutleryOpted ? BOOLEAN_AS_STRING.FALSE : BOOLEAN_AS_STRING.TRUE;
        await doUpdateCart(buildCutleryUpdatePayload(localCart, isMROFlow(), restaurantId, isOptedValue));
    }, [clickEvent, doUpdateCart, isCutleryOpted, localCart, restaurantId]);

    useEffect(() => {
        if (showCutleryTile) {
            impressionCutleryOptInWidget(impressionEvent, isCutleryOpted ? 1 : 0);
        }
    }, [impressionEvent, isCutleryOpted, showCutleryTile, shouldProceed]);

    useEffect(() => {
        if (!isGiftingCartSource(data?.cartSource)) {
            setIsFtueForCutlery(shouldProceed());
        }
    }, [data?.cartSource, shouldProceed]);

    return {
        isCutleryOpted,
        toggleCutlery,
        showCutleryTile,
        isFtueForCutlery: showCutleryTile && isFtueForCutlery,
        handleNudgeClosed,
        nudgeClosed,
    };
}
